import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
	Avatar,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	IconButton,
	List,
	Pagination,
	Paper,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useGetList, useTranslate } from "react-admin";

const ModerationEntryActivities = (props) => {
	const [page, setPage] = useState(1);
	const perPage = 5;

	const { data, isLoading, total } = useGetList("moderation_activities", {
		filter: { moderation_entry_id: props.moderationEntryId },
		pagination: { page, perPage },
		sort: { field: "created_at", order: "DESC" },
	});

	const translate = useTranslate();
	const totalPages = total ? Math.ceil(total / perPage) : 0;

	const handlePageChange = (event, value) => {
		setPage(value);
	};

	if (isLoading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			{data && data.length > 0 ? (
				<>
					<List
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 3,
						}}
					>
						{data.map((activity) => (
							<ModerationLog key={activity.identifier} activity={activity} />
						))}
					</List>

					{totalPages > 1 && (
						<Stack spacing={2} sx={{ mt: 3, alignItems: "center" }}>
							<Pagination
								count={totalPages}
								page={page}
								onChange={handlePageChange}
								color="primary"
								size="medium"
							/>
						</Stack>
					)}
				</>
			) : (
				<Typography variant="body1" sx={{ p: 2, textAlign: "center" }}>
					{translate("ra.navigation.no_results")}
				</Typography>
			)}
		</Box>
	);
};

const ModerationLog = ({ activity }) => {
	const translate = useTranslate();
	const theme = useTheme();
	const [showContent, setShowContent] = useState(false);

	const getStatusColor = (status) => {
		switch (status) {
			case "accepted":
				return theme.palette.success.main;
			case "rejected":
				return theme.palette.error.main;
			case "pending":
				return theme.palette.warning.main;
			default:
				return theme.palette.info.main;
		}
	};

	const toggleContent = () => {
		setShowContent(!showContent);
	};

	return (
		<Paper elevation={1} sx={{ padding: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
				<Avatar
					sx={{
						bgcolor:
							activity.mode === "manual"
								? theme.palette.primary.main
								: theme.palette.secondary.main,
						mr: 2,
					}}
				>
					{activity.mode === "manual" ? <PersonIcon /> : <AutoFixHighIcon />}
				</Avatar>

				<Box>
					<Typography variant="subtitle1">
						{activity.mode === "manual"
							? `${activity.user.first_name} ${activity.user.last_name}`
							: translate("resources.moderation_activities.mode.auto_long", {
									provider: activity.mode,
								})}
					</Typography>
				</Box>

				<Typography
					variant="body2"
					color="text.secondary"
					marginLeft="auto"
					fontSize="12px"
				>
					{dayjs(activity.created_at).format("DD-MM-YYYY HH:mm")}
				</Typography>
			</Box>

			<Box sx={{ mb: 2 }}>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<Chip
						label={translate(
							`resources.comments.status.${activity.previous_status}`,
						)}
						size="small"
						sx={{
							bgcolor: `${getStatusColor(activity.previous_status)}20`,
							color: getStatusColor(activity.previous_status),
							mr: 1,
						}}
					/>

					<Box component="span" sx={{ mx: 1 }}>
						→
					</Box>

					<Chip
						label={translate(
							`resources.comments.status.${activity.new_status}`,
						)}
						size="small"
						sx={{
							bgcolor: `${getStatusColor(activity.new_status)}20`,
							color: getStatusColor(activity.new_status),
						}}
					/>
				</Box>
			</Box>

			{activity.content && (
				<Box sx={{ mb: 2 }}>
					<Button
						startIcon={showContent ? <VisibilityOffIcon /> : <VisibilityIcon />}
						onClick={toggleContent}
						size="small"
						color="primary"
						variant="outlined"
						sx={{ mb: 1 }}
					>
						{showContent
							? translate(
									"resources.moderation_activities.actions.hide_content",
								)
							: translate(
									"resources.moderation_activities.actions.show_content",
								)}
					</Button>
					<Collapse in={showContent}>
						<Paper
							variant="outlined"
							sx={{
								p: 2,
								mt: 1,
								bgcolor: theme.palette.grey[50],
								borderColor: theme.palette.divider,
								maxWidth: "500px",
							}}
						>
							<Typography variant="body2">{activity.content}</Typography>
						</Paper>
					</Collapse>
				</Box>
			)}

			<Divider sx={{ my: 1.5 }} />

			<Box sx={{ mt: 1.5 }}>
				{activity.notes ? (
					<>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
							Notes :
						</Typography>
						<Typography
							variant="body1"
							sx={{
								fontStyle: "italic",
								pl: 1,
								borderLeft: `3px solid ${theme.palette.divider}`,
							}}
						>
							{activity.notes}
						</Typography>
					</>
				) : (
					<Typography variant="body2" color="text.secondary">
						{translate("resources.moderation_activities.label.no_notes")}
					</Typography>
				)}
			</Box>
		</Paper>
	);
};

export default ModerationEntryActivities;
